import { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { getSeaports } from "api";
import errorToast from "helpers/errorToast";
import FormField from "components/molecules/FormField/FormField";
import { handleRenderAddressOption } from "helpers/schedulehelper";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import { ICountryStates, ICountries } from "pages/shipper/RequestForm/components/Schedule/Types";
import { IPortAddress } from "../ScheduleTypes";

interface IPortInfoProps {
  prefix: string;
  countries: ICountries[];
  fieldPrefix?: string;
}

function PortInfo(props: IPortInfoProps) {
  const { prefix, countries, fieldPrefix = "schedule." } = props;

  const intl = useIntl();
  const methods = useFormContext();
  const { setValue, getValues, watch, trigger } = methods;

  const [searchPort, setSearchPort] = useState("");
  const [portAddress, setPortAddress] = useState<IPortAddress[]>([]);

  const arrivalPortCode = getValues(`${fieldPrefix}arrival_port_code`);
  const departurePortCode = getValues(`${fieldPrefix}departure_port_code`);

  const country = watch(`${fieldPrefix}${prefix}_country_name`);

  useEffect(() => {
    if (arrivalPortCode && prefix === "arrival") {
      setSearchPort(arrivalPortCode);
    }
    if (departurePortCode && prefix === "departure") {
      setSearchPort(departurePortCode);
    }
  }, [arrivalPortCode, departurePortCode]);

  useEffect(() => {
    const countryCode = countries.filter(
      (cy) => cy.label?.toLowerCase() === watch(`${fieldPrefix}${prefix}_country_name`)?.toLowerCase(),
    );
    if (countryCode?.length > 0) {
      setValue(`${fieldPrefix}${prefix}_country_code`, countryCode?.[0]?.value);
    }
  }, [country]);

  useEffect(() => {
    // debounce 300ms for seaport search
    if (searchPort) {
      const timeoutId = setTimeout(() => {
        handleAddressTypeOption();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [searchPort]);

  const handleSelectPort = (item: IPortAddress[]) => {
    setValue(`${fieldPrefix}${prefix}_port_code`, item?.[0]?.code || item?.[0]?.un_locode);
    setValue(`${fieldPrefix}${prefix}_city`, item?.[0]?.city_name || "");
    setValue(`${fieldPrefix}${prefix}_country_name`, item?.[0]?.country_name || "");
    setPortAddress([]);
  };

  const handleSelectCountry = (item: ICountryStates[]) => {
    setValue(`${fieldPrefix}${prefix}_country_code`, item?.[0]?.code || "");
  };

  const handleAddressTypeOption = () => {
    getSeaports(searchPort)
      .then((res) => {
        const data = res.data.results;
        setPortAddress(data);
        const portDetail = data.filter((pt: IPortAddress) => pt.un_locode === searchPort);

        if (portDetail?.length > 0) {
          const { name, un_locode, country_name } = portDetail[0];
          setValue(`${fieldPrefix}${prefix}_port_name`, name);
          setValue(`${fieldPrefix}${prefix}_port_code`, un_locode);
          setValue(`${fieldPrefix}${prefix}_country_name`, country_name);
        }
      })
      .catch((error) => errorToast(error));
  };

  return (
    <div className="flex-row">
      <div className="w-192 dropdown-div">
        <SearchSelect
          shouldUnregister
          preselect={false}
          name={`${fieldPrefix}${prefix}_port_name`}
          defaultValue={getValues(`${fieldPrefix}${prefix}_port_name`)}
          placeholder={intl.formatMessage({
            id: "report.search",
            defaultMessage: "Search",
          })}
          label={intl.formatMessage(
            prefix === "departure"
              ? {
                  id: "quote.provider.departurePort",
                  defaultMessage: "Departure port",
                }
              : {
                  id: "quote.provider.arrivalPort",
                  defaultMessage: "Arrival port",
                },
          )}
          onInputChange={(val: string) => {
            setSearchPort(val);
          }}
          options={portAddress.map((option: IPortAddress) => ({
            id: option.id,
            label: option.name,
            value: option.name,
            city_name: option.city_name,
            country_name: option.country_name,
            country_code: option.country_code,
            code: option.code || option.un_locode,
          }))}
          onSelect={(item: IPortAddress[]) => {
            handleSelectPort(item);
            trigger(`${fieldPrefix}${prefix}_city`);
            trigger(`${fieldPrefix}${prefix}_country_name`);
          }}
          filterBy={["label", "city_name", "country_name", "code"]}
          handleRenderOption={handleRenderAddressOption}
          required
        />
      </div>
      <div className="w-192">
        <FormField
          id={`id_${prefix}_city`}
          validations={{
            shouldUnregister: true,
          }}
          label={intl.formatMessage(
            prefix === "departure"
              ? {
                  id: "quote.provider.departureCity",
                  defaultMessage: "Departure city",
                }
              : {
                  id: "nquote.provider.arrivalCity",
                  defaultMessage: "Arrival city",
                },
          )}
          type="textinput"
          field_id={`${fieldPrefix}${prefix}_city`}
          mandatory
        />
      </div>
      <div className="w-192">
        <label className="label required">
          {prefix === "departure" ? (
            <FormattedMessage id="quote.provider.departureCountry" defaultMessage="Departure country" />
          ) : (
            <FormattedMessage id="quote.provider.arrivalCountry" defaultMessage="Arrival country" />
          )}
        </label>
        <SearchSelect
          shouldUnregister
          name={`${fieldPrefix}${prefix}_country_name`}
          defaultValue={getValues(`${fieldPrefix}${prefix}_country_name`)}
          placeholder={intl.formatMessage({
            id: "general.country",
            defaultMessage: "Country",
          })}
          options={countries}
          onSelect={(item: ICountryStates[]) => {
            handleSelectCountry(item);
          }}
          required
        />
      </div>
    </div>
  );
}

export default PortInfo;
